import { PlaybookReportType } from '@driftt/api-types'
import PlaybookHistoryModal from '@driftt/playbook-components/dist/PlaybookHistoryModal'
import {
  Breadcrumbs,
  H3,
  InlineEditInput,
  NavigationHeader,
} from '@driftt/tide-core'
import { EducationTooltip } from '@driftt/tide-tooltips'
import {
  receiveUserSettings,
  showTransientNotification,
} from '@driftt/traffic-control'
import moment from 'moment'
import { isEmpty, isNil, omit, pathOr } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'

import { savePlaybook } from 'api/playbooks'
import {
  createTask,
  deleteTask,
  getTasksForPlaybook,
  updateTask,
} from 'api/tasks'
import { updateUserProperties } from 'api/user'
import PlaybookSchedulerModal from 'components/PlaybookSchedulerModal'
import PlaybookVersionSelect from 'components/PlaybookVersionSelect'
import { TIMEZONE_TO_ABBR } from 'constants/timezones'
import { userHasChatPlaybooksWrite } from 'utils/access'
import { isPlaybookDirty } from 'utils/comparer'

import generateNavItems from './generateNavItems'
import SubNav from './SubNav'

import './style.css'

function isNullOrEmpty(val) {
  return isNil(val) || isEmpty(val)
}

const BREADCRUMB_HEADINGS = {
  DEFAULT: [
    {
      title: 'Playbooks',
      link: '/playbooks',
    },
    {
      title: `Chat`,
      link: '/playbooks/list/chat',
    },
    {
      title: `Edit playbook`,
      link: '/',
      isActive: true,
    },
  ],
  [PlaybookReportType.FORM_SUBMISSION_FAST_LANE]: [
    {
      title: 'Playbooks',
      link: '/playbooks',
    },
    {
      title: `Fastlane`,
      link: '/playbooks/list/fastlane',
    },
    {
      title: `Edit playbook`,
      link: '/',
      isActive: true,
    },
  ],
  [PlaybookReportType.MEETING_BOOKING_BOT]: [
    {
      title: 'Playbooks',
      link: '/playbooks',
    },
    {
      title: `Meeting`,
      link: '/playbooks/list/meetings',
    },
    {
      title: `Edit playbook`,
      link: '/',
      isActive: true,
    },
  ],
}

function EditorNavigation({
  autoSaving,
  cancelPreview,
  draftPlaybook,
  id,
  isDriftLinkOnly,
  isPreviewState,
  history,
  location,
  publishedPlaybook,
  revertPlaybook,
  selectedPlaybookVersion,
  targeting,
  updatePlaybookAfterPublish,
  updatePlaybookAfterStatusChange,
  updateSelectedPlaybookVersion,
  versionTimestamps,
  clpDomain,
  showOnboardingPlaybookTooltip,
  userId,
  onReceiveUserSettings,
  widgetInstalled,
  onReceivePlaybook,
  showTransientNotification,
  landingPageCname,
  landingPageCnameStatus,
  landingPageSettings,
  landingPageSubdomain,
  notifications,
  onCreateExperiment,
  onDeleteExperiment,
  onChangeAIEnabled,
  isAIEnabled,
  isBot,
  trackPublishAndResetTimer,
  isFastlane,
  isFastlaneQuickSetup,
  isMeetingBooking,
  playbookReportType,
}) {
  const isVisualBuilder = location.pathname.includes('visual-builder')
  const [showSchedulerModal, setShowSchedulerModal] = useState(false)
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false)
  const [scheduledTasks, setScheduledTasks] = useState([])
  const isDirty = useMemo(
    () => isPlaybookDirty(publishedPlaybook, draftPlaybook),
    [publishedPlaybook, draftPlaybook]
  )

  const isEnterprise = useMemo(() => {
    const skills = pathOr(
      [],
      ['actions', '0', 'action', 'skills'],
      publishedPlaybook
    )
    return skills.some(({ type }) => type === 'ENTERPRISE')
  }, [publishedPlaybook])

  const headings =
    BREADCRUMB_HEADINGS[playbookReportType] || BREADCRUMB_HEADINGS.DEFAULT

  const navItems = useMemo(() => {
    if (!userHasChatPlaybooksWrite()) {
      return []
    }

    return generateNavItems({
      selectedPlaybookVersion: selectedPlaybookVersion,
      draftPlaybook: draftPlaybook,
      playbook: publishedPlaybook,
      isDriftLinkOnly,
      isEnterprise,
      versionTimestamps,
      isFastlane,
      isFastlaneQuickSetup,
      isMeetingBooking,
    })
  }, [
    selectedPlaybookVersion,
    draftPlaybook,
    publishedPlaybook,
    isDriftLinkOnly,
    isEnterprise,
    versionTimestamps,
    isFastlane,
    isFastlaneQuickSetup,
    isMeetingBooking,
  ])

  useEffect(() => {
    const doFetchTasks = async () => {
      try {
        const { data } = await getTasksForPlaybook(id)
        setScheduledTasks(data)
      } catch (err) {
        console.warn(err)
      }
    }

    doFetchTasks()
  }, [id])

  const closeOnboardingPlaybookTooltip = () => {
    updateUserProperties(userId, {
      showOnboardingPlaybookTooltip: false,
    }).then(({ data }) => {
      onReceiveUserSettings({ ...data, showOnboardingPlaybookTooltip: false })
    })
  }

  const persistPlaybookName = async (name) => {
    try {
      const { data } = await savePlaybook({
        playbook: {
          name,
          ...omit(['name'], publishedPlaybook),
        },
      })

      updatePlaybookAfterStatusChange(data)
    } catch (err) {
      console.warn(err)
    }
  }

  const playbookNameDisplay = isNullOrEmpty(selectedPlaybookVersion)
    ? () => (
        <EducationTooltip
          className="playbook-editor-nav-header--education-tooltip"
          onClose={closeOnboardingPlaybookTooltip}
          showTooltip={showOnboardingPlaybookTooltip}
          tooltipSide="bottom"
          tooltipCaretLocation="left"
          headerCopy={
            <div className="playbook-editor-nav-header--education-tooltip__header">
              Here it is — your first bot!
            </div>
          }
          bodyCopy={
            <div className="playbook-editor-nav-header--education-tooltip__body">
              You can customize and optimize your bot further from here!
            </div>
          }
          buttonInfoArray={[
            {
              copy: 'Woohoo!',
              func: closeOnboardingPlaybookTooltip,
            },
          ]}
        >
          {userHasChatPlaybooksWrite() ? (
            <InlineEditInput
              className="playbook-name-input"
              headingSize="h3"
              onSubmit={persistPlaybookName}
              type="heading"
              toolTipPlacement="down"
              value={publishedPlaybook.name}
              shouldValidate
              withSave
            />
          ) : (
            <H3 className="playbook-name-non-editable">
              {publishedPlaybook.name}
            </H3>
          )}
        </EducationTooltip>
      )
    : selectedPlaybookVersion.name

  async function doPersistTask(task) {
    try {
      if (scheduledTasks.length > 0) {
        const { data } = await updateTask(task, id, scheduledTasks[0].action)
        setScheduledTasks([data])
      } else {
        const { data } = await createTask(
          {
            ...task,
            playbookId: id,
          },
          id
        )
        setScheduledTasks([data])
      }
      notifications.success(
        `Your playbook will be disabled on ${moment(task.scheduledTime).format(
          'MM/DD/YY [at] h:mm A'
        )} ${TIMEZONE_TO_ABBR[task.timeZone]}`
      )
    } catch (err) {
      console.warn(err)
    }
  }

  async function doDeleteTask(taskId) {
    try {
      await deleteTask(id, taskId)
      setScheduledTasks([])
    } catch (err) {
      console.warn(err)
    }
  }

  return (
    <>
      <NavigationHeader
        nav={navItems}
        history={history}
        secondary={<Breadcrumbs headings={headings} />}
        title={playbookNameDisplay}
      >
        {userHasChatPlaybooksWrite() && (
          <PlaybookVersionSelect
            publishedPlaybook={publishedPlaybook}
            onUpdateStatus={updatePlaybookAfterStatusChange}
            onShowSchedulerModal={() => setShowSchedulerModal(true)}
            onShowVersionHistoryModal={() => setShowVersionHistoryModal(true)}
            scheduledTask={scheduledTasks[0]}
            onDeleteTask={doDeleteTask}
          />
        )}
      </NavigationHeader>

      {isVisualBuilder && userHasChatPlaybooksWrite() && (
        <SubNav
          autoSaving={autoSaving}
          draftPlaybook={draftPlaybook}
          id={id}
          isPlaybookDirty={isDirty}
          isPreviewState={isPreviewState}
          location={location}
          targeting={targeting}
          updatePlaybookAfterPublish={updatePlaybookAfterPublish}
          clpDomain={clpDomain}
          publishedPlaybook={publishedPlaybook}
          widgetInstalled={widgetInstalled}
          onReceivePlaybook={onReceivePlaybook}
          showTransientNotification={showTransientNotification}
          landingPageCname={landingPageCname}
          landingPageCnameStatus={landingPageCnameStatus}
          landingPageSettings={landingPageSettings}
          landingPageSubdomain={landingPageSubdomain}
          onCreateExperiment={onCreateExperiment}
          onDeleteExperiment={onDeleteExperiment}
          onChangeAIEnabled={onChangeAIEnabled}
          isAIEnabled={isAIEnabled}
          isBot={isBot}
          trackPublishAndResetTimer={trackPublishAndResetTimer}
          updateStatus={updatePlaybookAfterStatusChange}
        />
      )}
      {showSchedulerModal &&
        ReactDOM.createPortal(
          <PlaybookSchedulerModal
            onSubmit={doPersistTask}
            scheduledTask={scheduledTasks.length > 0 ? scheduledTasks[0] : null}
            onClose={() => setShowSchedulerModal(false)}
          />,
          document.getElementById('pew-modal-root')
        )}
      <PlaybookHistoryModal
        open={showVersionHistoryModal}
        onClose={() => setShowVersionHistoryModal(false)}
        publishedPlaybook={publishedPlaybook}
        activePlaybook={selectedPlaybookVersion}
        onSelectPlaybookVersion={updateSelectedPlaybookVersion}
        onRevertToPlaybook={revertPlaybook}
        onCancelPreview={cancelPreview}
      />
    </>
  )
}

/* ENHANCE ME */
const mapStateToProps = (state) => {
  return {
    showOnboardingPlaybookTooltip: pathOr(
      false,
      ['user', 'settings', 'showOnboardingPlaybookTooltip'],
      state
    ),
    userId: state.user.id,
    isBot: state.user.bot,
  }
}

const mapDispatchToProps = (dispatch) => ({
  onReceiveUserSettings: (settings) => {
    dispatch(receiveUserSettings(settings))
  },
  notifications: {
    error: (message) =>
      dispatch(showTransientNotification({ message, type: 'error' })),
    success: (message) =>
      dispatch(showTransientNotification({ message, type: 'success' })),
  },
})
export default connect(mapStateToProps, mapDispatchToProps)(EditorNavigation)
